import React, { Fragment, ReactNode } from 'react';
import { Nullable } from 'global';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { ArrowForwardIos, Warning } from '@mui/icons-material';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { Stage, StageId } from '../api';
import { StageBadge } from './StageBadge';
import { AttributeRenderers, LoadingProps } from './types';
import { StageAttributes } from './StageAttributes';
import { checkIsStageFieldsExist, determineStatus } from '../lib';

interface Props {
  activeStage: StageId;
  viewedStage: Nullable<StageId>;
  loading: LoadingProps['loading'];
  stages: Record<StageId, Stage>;
  changeStageStatusSlot: ReactNode;
  onViewStage: (stage: Stage) => void;
  attributeRenderers: AttributeRenderers;
}

export const Details = ({
  stages,
  loading,
  viewedStage,
  activeStage,
  onViewStage,
  attributeRenderers,
  changeStageStatusSlot,
}: Props) => (
  <Box className="managerDetails">
    <Box className="stagesList">
      {Object.values(stages).map((stage, index, arr) => (
        <Fragment key={stage.businessProcessFlowStageId}>
          <StageBadge
            stage={stage}
            onClick={onViewStage}
            isUpdating={loading.isBPFsUpdating}
            status={determineStatus(stages, stage, activeStage, viewedStage)}
          />

          <RenderWithCondition condition={index !== arr.length - 1}>
            <ArrowForwardIos sx={{ fontSize: '1rem' }} />
          </RenderWithCondition>
        </Fragment>
      ))}
    </Box>

    <Stack className="attributesLayout">
      <Box className="attributesHeader">
        <Box className="labelWithWarning">
          <RenderWithCondition condition={!checkIsStageFieldsExist(stages[activeStage])}>
            <Tooltip arrow placement="top-start" title="Stage Attributes are not configured">
              <Warning color="warning" />
            </Tooltip>
          </RenderWithCondition>

          <Typography component="span" variant="subtitle2">
            Stage Attributes
          </Typography>
        </Box>

        {changeStageStatusSlot}
      </Box>

      <RenderWithCondition condition={checkIsStageFieldsExist(stages[activeStage])}>
        <Box className="attributes">
          <StageAttributes
            isLoading={loading.isStagesUpdating}
            attributeRenderers={attributeRenderers}
            stage={stages[viewedStage ?? activeStage]}
          />
        </Box>
      </RenderWithCondition>
    </Stack>
  </Box>
);
